import React, { useCallback, useEffect, useState } from 'react'
import { Message } from 'rsuite'
import CustomIcons from '../../../ui_reusable/CustomIcons'
import EditUser from './EditUser'

import useStandardToaster from '../../../ui_reusable/StandardToaster'
import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import { VerySmallNormalTag } from '../../../ui_reusable/styles/Tags.styles'
import { ExpansiveListInVersatileGrid } from '../../../ui_reusable/VersatileGridUtils'
import {
  GridCellBoldText,
  GridCellSmallFadedText,
  GridCellNormalText,
  ListItemInVersatileGrid,
} from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import {
  UserActionsContainer,
  ToggleContainer,
  RadioContainer,
  ResendLink,
} from './styles/ClientUserList.styles'
import {
  StandardRadio,
  RadioProvider,
} from '../../../ui_reusable/StandardRadioButtons'
import { EasyIconButton } from '../../../ui_reusable/styles/Button.styles'
import { StandardToggleButton } from '../../../ui_reusable/styles/ToggleButton.styles'
import Modal, { ModalDefaultButtonFooter } from '../../../ui_reusable/Modal'
import { StandardCheckbox } from '../../../ui_reusable/StandardInputs'
import { useAuth } from '../../../../context/AuthContext'

const PAGE_SIZE = 10

const ListUser = ({ clientId, refresh, setRefresh }) => {
  const { isAdmin } = useAuth()
  const id = clientId
  const [clientUsers, setClientUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const { pushToast } = useStandardToaster()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showClientContactWarning, setShowClientContactWarning] =
    useState(false)
  const [clientProjects, setClientProjects] = useState([])
  const [showEditModal, setShowEditModal] = useState(false)

  // Flatten the data structure
  const flattenUserData = (users) => {
    return users.map((userData) => ({
      ...userData.user,
      is_active: userData.is_active,
      role: userData.role,
      is_client_contact: userData.is_client_contact,
      client: userData.client || {},
      projects: userData.projects || [], // Include projects array
      _original: userData, // Keep original data for reference
    }))
  }

  useEffect(() => {
    const abortController = new AbortController()

    const getClientUsers = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })
        if (search) {
          params.set('search', search)
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/${id}/users?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages (e.g. last user on the page was
        // deleted) — step back to the previous page and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const res = await response.json()
        const users = res.data || []

        setClientUsers(flattenUserData(Array.isArray(users) ? users : []))
        setTotal(res.total ?? (Array.isArray(users) ? users.length : 0))
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch client users:', error)
          setClientUsers([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    if (!id) {
      setLoading(false)
      return
    }

    getClientUsers()

    return () => {
      abortController.abort()
    }
  }, [id, refresh, page, search])

  useEffect(() => {
    const abortController = new AbortController()

    const getProjects = async () => {
      if (!id) return
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/?client=${id}&ordering=name`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const res = await response.json()

        setClientProjects(res.data || [])
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch client projects:', error)
          setClientProjects([])
        }
      }
    }

    getProjects()

    return () => {
      abortController.abort()
    }
  }, [id])

  // Handle toggle active
  const handleToggleActive = async (userId, currentState) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/${id}/users/${userId}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            is_active: !currentState,
            user: { is_active: !currentState },
          }),
        },
      )

      if (response.ok) {
        setRefresh(new Date().getTime())
        pushToast(
          <Message type="success">User status updated successfully</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        throw new Error('Failed to update user status')
      }
    } catch (error) {
      console.error('Failed to toggle user status:', error)
      pushToast(<Message type="error">Failed to update user status</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    }
  }

  // Handle toggle client contact
  const handleToggleClientContact = async (userId) => {
    try {
      // First, set the selected user as the client contact
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/${id}/users/${userId}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ is_client_contact: true }),
        },
      )

      if (!response.ok) {
        throw new Error('Failed to update client contact status')
      }

      // Unset other users' client contact status
      const otherUsers = clientUsers.filter(
        (user) => user.id !== userId && user.is_client_contact,
      )

      for (const otherUser of otherUsers) {
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/${id}/users/${otherUser.id}/`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ is_client_contact: false }),
          },
        )
      }

      setRefresh(new Date().getTime())
      pushToast(
        <Message type="success">Client contact updated successfully</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } catch (error) {
      console.error('Failed to update client contact status:', error)
      pushToast(
        <Message type="error">Failed to update client contact status</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  // Handle showing delete confirmation modal
  const handleShowDeleteModal = (user) => {
    if (user.is_client_contact) {
      setSelectedUser(user)
      setShowClientContactWarning(true)
    } else {
      setSelectedUser(user)
      setShowDeleteModal(true)
    }
  }

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/${id}/users/${selectedUser.id}/`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )

      // Check specifically for 204 status code which indicates successful deletion
      if (response.status === 204) {
        setRefresh(new Date().getTime())
        setShowDeleteModal(false)
        pushToast(
          <Message type="success">User deleted successfully.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        throw new Error('Failed to delete user')
      }
    } catch (error) {
      console.error('Failed to delete user:', error)
      pushToast(<Message type="error">{error.message}</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    }
  }

  const handleProjectAccessChange = async (
    userId,
    projectId,
    originalData,
    checked,
  ) => {
    try {
      if (!checked) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/users/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              client_user_id: originalData._original.id,
            }),
          },
        )

        if (response.ok) {
          setRefresh(Date.now())
          pushToast(
            <Message type="success">
              Project access updated successfully
            </Message>,
            { placement: 'topCenter', duration: 5000 },
          )
        } else {
          const errorData = await response.json()

          throw new Error(
            errorData.message || 'Failed to update project access',
          )
        }
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/users/${userId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )

        if (response.ok) {
          setRefresh(Date.now())
          pushToast(
            <Message type="success">
              Project access updated successfully
            </Message>,
            { placement: 'topCenter', duration: 5000 },
          )
        } else {
          const errorData = await response.json()

          throw new Error(
            errorData.message || 'Failed to update project access',
          )
        }
      }
    } catch (error) {
      console.error('Failed to update project access:', error)
      pushToast(<Message type="error">{error.message}</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    }
  }

  const handleShowEditModal = (user) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setRefresh(new Date().getTime()) // Ensure list refreshes when modal closes
  }

  const handleResendEmail = async (userId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/${id}/users/${userId}/resend-invitation/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )

      const res = await response.json()

      if (response.ok) {
        pushToast(<Message type="success">User invite has been sent</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      } else {
        throw new Error(res.message || 'Failed to resend invitation')
      }
    } catch (error) {
      console.error('Failed to resend:', error)
      pushToast(<Message type="error">{error.message}</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
    }
  }

  const headers = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: false,
      isSearchable: true,
    },
    {
      title: 'Role',
      key: 'role',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Email',
      key: 'email',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Project Access',
      key: 'project_access',
      type: 'none',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Active',
      key: 'is_active',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      valueMapping: {
        true: 'active',
        false: 'inactive',
      },
      borderLeft: true,
      center: true,
    },
    {
      title: 'Client Contact',
      key: 'is_client_contact',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      valueMapping: {
        true: 'contact',
        false: 'not contact',
      },
      borderLeft: true,
      center: true,
    },

    {
      title: 'Actions',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      borderLeft: true,
    },
  ]

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  // Find the ID of the user who is the client contact
  const clientContactId =
    clientUsers.find((user) => user.is_client_contact)?.id || ''

  return (
    <RadioProvider
      group="clientContact"
      initValue={clientContactId}
      onChange={(value) => handleToggleClientContact(value)}
    >
      <VersatileGrid
        headers={headers}
        data={clientUsers}
        renderRow={(rowData, rowIdx) => {
          return [
            <GridCellBoldText>{rowData.name}</GridCellBoldText>,
            <GridCellNormalText>
              <VerySmallNormalTag>{rowData.role}</VerySmallNormalTag>
            </GridCellNormalText>,
            <GridCellSmallFadedText>
              {rowData.email}
              {isAdmin && !rowData.is_active && (
                <ResendLink onClick={() => handleResendEmail(rowData.id)}>
                  Click here to resend user invite email
                </ResendLink>
              )}
            </GridCellSmallFadedText>,
            <ExpansiveListInVersatileGrid>
              {(() => {
                const isManagerOrAdmin =
                  rowData.role === 'manager' || rowData.role === 'admin'
                const userProjectIds = new Set(
                  (rowData.projects || []).map((project) => project.id),
                )
                const hasAllProjects =
                  clientProjects.length > 0 &&
                  clientProjects.every((project) =>
                    userProjectIds.has(project.id),
                  )
                const shouldDisable = isManagerOrAdmin && hasAllProjects

                return clientProjects.map((project) => {
                  const isInAssociation = userProjectIds.has(project.id)
                  return (
                    <ListItemInVersatileGrid key={project.id}>
                      <StandardCheckbox
                        disabled={shouldDisable}
                        checked={isInAssociation}
                        onChange={() =>
                          handleProjectAccessChange(
                            rowData.id,
                            project.id,
                            rowData,
                            isInAssociation,
                          )
                        }
                      />
                      {project.name}
                    </ListItemInVersatileGrid>
                  )
                })
              })()}
            </ExpansiveListInVersatileGrid>,

            <ToggleContainer>
              <StandardToggleButton
                checked={rowData.is_active}
                onChange={() =>
                  handleToggleActive(rowData.id, rowData.is_active)
                }
              />
            </ToggleContainer>,
            <RadioContainer>
              <StandardRadio group="clientContact" value={rowData.id} />
            </RadioContainer>,

            <UserActionsContainer>
              <EasyIconButton onClick={() => handleShowDeleteModal(rowData)}>
                <CustomIcons.TrashIcon size="xs" />
              </EasyIconButton>
              <EasyIconButton
                onClick={() => handleShowEditModal(rowData._original)}
              >
                <CustomIcons.PenIcon size="xs" />
              </EasyIconButton>
            </UserActionsContainer>,
          ]
        }}
        maxDisplayedItems={PAGE_SIZE}
        serverSide
        serverTotal={total}
        serverPage={page}
        onPageChange={handlePageChange}
        onSearchChange={handleSearchChange}
        loading={loading}
        loadingMessage={'Loading users...'}
      />

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header onClose={() => setShowDeleteModal(false)}>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete user{' '}
          <strong>{selectedUser?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmColor: '#b71c1c',
            onConfirm: handleDeleteUser,
            onCancel: () => setShowDeleteModal(false),
          })}
        </Modal.Footer>
      </Modal>

      {/* Client Contact Warning Modal */}
      <Modal
        open={showClientContactWarning}
        onClose={() => setShowClientContactWarning(false)}
      >
        <Modal.Header onClose={() => setShowClientContactWarning(false)}>
          <Modal.Title>Cannot Delete Client Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The user <strong>{selectedUser?.name}</strong> is selected as a client
          contact and can't be deleted. In order to delete this contact, first
          select another client contact.
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'OK',
            onConfirm: () => setShowClientContactWarning(false),
          })}
        </Modal.Footer>
      </Modal>

      {/* Edit Client User Modal */}
      <EditUser
        user={selectedUser}
        clientId={id}
        setRefresh={setRefresh}
        open={showEditModal}
        onClose={handleCloseEditModal}
      />
    </RadioProvider>
  )
}

export default ListUser
